import {LobbyImage} from '@colonist/types'
import {TimeUtils} from '@colonist/utils'
import {AnalyticEventType, AnalyticEventCategory} from '../../../../../shared/entity_datas/EntityDatas'
import {CA} from '../../analytics/Analytics'
import {getReplayUrlSlug, REPLAY_URL_ACTION_SEARCH_PARAM_NAME} from '../ReplayUrlUtilities'
import {UIReplayButton} from './UIReplayButton'
import {TranslationObject} from '@colonist/i18n'
import {UIReplayController} from '../UIReplayController'
import {ClientUserState} from '../../ClientUserState'
import {UIAjaxWrapper} from '../../network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../network/UIAPIEndpoints'
import {PlayerColor} from '@colonist/game-states'
import {createLinkIn} from '../../ui_utils/DomUtils'
import {createTooltipIn} from '../../ui_utils/TooltipUtils.ts'

export class UIReplayShareButton extends UIReplayButton {
    private readonly replayController: UIReplayController

    constructor(replayController: UIReplayController, container: HTMLDivElement) {
        const onClick = () => this.onShareReplayButtonClick()
        const tooltip: TranslationObject = {key: 'strings:game.tooltips.replay.shareReplay'}
        super(container, LobbyImage.Share, onClick, {tooltip: tooltip})

        this.replayController = replayController
    }

    private async onShareReplayButtonClick() {
        void CA.sendAnalyticEvent({
            type: AnalyticEventType.ReplayClickedCreateShareableLink,
            category: AnalyticEventCategory.Replay,
            floatValue: this.replayController.replayAccessMethod,
        })

        void this.shareReplay()
    }

    protected async shareReplay() {
        const TEXT_TYPE = 'text/plain'
        const getShareableLinkBlob = async() => {
            const isShareableLinkAlreadyInUrl = getReplayUrlSlug(window.location.href) !== undefined
            let shareableLink = isShareableLinkAlreadyInUrl ? location.href : await this.getShareableLink()
            if(shareableLink == undefined) return new Blob([''], {type: TEXT_TYPE})

            const currentActionIndex = this.replayController.getCurrentAction()
            shareableLink = UIReplayShareButton.addActionToShareableLink(shareableLink, currentActionIndex)

            return new Blob([shareableLink], {type: TEXT_TYPE})
        }

        const shareableLinkClipboardItem = new ClipboardItem({
            [TEXT_TYPE]: getShareableLinkBlob(),
        })

        try {
            await navigator.clipboard.write([shareableLinkClipboardItem])
            this.flashTooltipCopyLinkSuccess()
        } catch{
            const linkBlob = await shareableLinkClipboardItem.getType(TEXT_TYPE)
            const linkHref = await linkBlob.text()
            const hiddenLink = createLinkIn(document.body, linkHref, {key: 'strings:colonistCommon.utils.emptyString'}, '')
            hiddenLink.target = '_blank'
            hiddenLink.click()
        }
    }

    private flashTooltipCopyLinkSuccess() {
        this.setAsset(LobbyImage.CheckMark)
        createTooltipIn(this.icon, {key: 'strings:game.tooltips.replay.copiedLink'})
        if(this.tooltip) this.tooltip.style.visibility = 'visible'

        const SHARE_REPLAY_BUTTON_REVERT_MS = TimeUtils.MILLISECONDS * 1.5
        setTimeout(() => {
            this.setAsset(LobbyImage.Share)
            createTooltipIn(this.icon, {key: 'strings:game.tooltips.replay.shareReplay'})
            if(this.tooltip) this.tooltip.style.visibility = ''
        }, SHARE_REPLAY_BUTTON_REVERT_MS)
    }

    protected async getShareableLink(): Promise<string | undefined> {
        const databaseGameId = this.replayController.replayData.databaseGameId
        const replayPerspectiveColor = this.getShareableLinkPreferredPerspective()
        const replayShareableLinkUrl = UIAPIEndpoints.apiReplayShareableLink(databaseGameId, replayPerspectiveColor.toString())
        const replayShareableLinkResponse = await UIAjaxWrapper.getRequestHandler(replayShareableLinkUrl, undefined)
        if(replayShareableLinkResponse == undefined) return undefined
        return replayShareableLinkResponse.json
    }

    private getShareableLinkPreferredPerspective(): PlayerColor {
        const playerUserStates = this.replayController.replayData.playerUserStates
        const replayPerspectiveColor = this.replayController.replayData.playerPerspective
        const replayPerspectivePlayerUserState = playerUserStates.find((playerUserState) => playerUserState.selectedColor === replayPerspectiveColor)
        const isSelectedPerspectivePlayedAsByMe = ClientUserState.userState?.id === replayPerspectivePlayerUserState?.userId
        if(isSelectedPerspectivePlayedAsByMe) return replayPerspectiveColor

        const anyPlayerUserStatePlayedAsByMe = playerUserStates.find((playerUserState) => playerUserState.userId === ClientUserState.userState?.id)
        if(anyPlayerUserStatePlayedAsByMe !== undefined) return anyPlayerUserStatePlayedAsByMe.selectedColor

        return replayPerspectiveColor
    }

    private static addActionToShareableLink(link: string, action: number): string {
        const isActionGameBeginning = action === -1
        if(isActionGameBeginning) return link

        const url = new URL(link)
        url.searchParams.set(REPLAY_URL_ACTION_SEARCH_PARAM_NAME, action.toString())
        return url.href
    }
}
